import AddProfileDialog from '@app/addProfileDialog';
import {SubProfileType} from '@common/auth/user';
import {apiClient, queryClient} from '@common/http/query-client';
import {message} from '@common/i18n/message';
import {closeDialog} from '@common/ui/overlays/store/dialog-store';
import {toast} from '@common/ui/toast/toast';
import {useMutation, useQuery} from '@tanstack/react-query';

type AddProfileRequest = {
  avatar_id?: number;
  name?: string;
  type: 'kid' | 'teenage' | 'adult';
};

export const useGetProfiles = () => {
  return useQuery({
    queryKey: ['profile-list'],
    queryFn: getProfiles,
  });
};

const getProfiles = (): Promise<SubProfileType[]> => {
  return apiClient.get('user-profile/profile-list').then(r => r.data);
};

export const useAddProfile = () => {
  return useMutation({
    mutationKey: ['create-profile'],
    mutationFn: (data: AddProfileRequest) => addProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['profile-list'],
      });
    },
  });
};

const addProfile = (data: AddProfileRequest) => {
  return apiClient
    .post('user-profile/create-profile', data)
    .then(res => res.data);
};

export const useSelectProfile = () => {
  return useMutation({
    mutationKey: ['select-profile'],
    mutationFn: (profile_id: number) => selectProfile(profile_id),
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });
};

const selectProfile = (profile_id: number) => {
  return apiClient
    .post('user-profile/select-profile', {profile_id})
    .then(res => res.data);
};

export const useDeleteProfile = () => {
  return useMutation({
    mutationKey: ['delete-profile'],
    mutationFn: (id: number) => deleteProfile(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['profile-list'],
      });
    },
  });
};

const deleteProfile = (id: number) => {
  return apiClient.delete(`user-profile/destroy-profile/${id}`);
};

export const useEditProfile = (id: number) => {
  return useMutation({
    mutationKey: ['edit-profile'],
    mutationFn: (data: AddProfileRequest) => editProfile(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['profile-list'],
      });
    },
  });
};

const editProfile = (id: number, data: AddProfileRequest) => {
  return apiClient.post(`user-profile/edit-profile/${id}`, data);
};
